import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import BrandLogo from '@/components/brand/BrandLogo';
import { BRAND } from '@/theme/brand';

const NAV = [
  { label: 'Home', to: '/' },
  { label: 'Book a Cab', hash: '#booking-form' },
  { label: 'Our Fleet', hash: '#fleet' },
  { label: 'Contact', hash: '#contact' },
];

const AtithiNavbar = () => {
  const [open, setOpen] = useState(false);
  const [heroScrolled, setHeroScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleHeroScroll = () => setHeroScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleHeroScroll);
    return () => window.removeEventListener('scroll', handleHeroScroll);
  }, []);

  const isActive = (item) => {
    if (item.hash) {
      return location.pathname === '/' && location.hash === item.hash;
    }
    if (item.to === '/') {
      return location.pathname === '/' && !location.hash;
    }
    return location.pathname === item.to;
  };

  const handleClick = (item, e) => {
    if (item.hash) {
      e.preventDefault();
      if (window.location.pathname !== '/') {
        navigate({ pathname: '/', hash: item.hash.slice(1) });
      } else {
        document.querySelector(item.hash)?.scrollIntoView({ behavior: 'smooth' });
        window.history.replaceState(null, '', item.hash);
      }
      setOpen(false);
    }
  };

  const linkClass = (item) => {
    const base = 'text-[16px] lg:text-[17px] font-bold px-4 py-2 transition-colors duration-200 whitespace-nowrap';
    if (isActive(item)) {
      return `${base} text-[#E8651A]`;
    }
    return heroScrolled
      ? `${base} text-gray-600 hover:text-[#1B2D5E]`
      : `${base} text-white/90 hover:text-white`;
  };

  const linkStyle = (item) =>
    !heroScrolled ? { textShadow: '0 1px 3px rgba(0,0,0,0.4)' } : undefined;

  return (
    <header
      className={`site-header fixed top-0 left-0 right-0 z-50 w-full font-body transition-all duration-300 ${
        heroScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="w-full flex items-center justify-between px-6 lg:px-10 py-5 lg:py-6">
        <div className="flex items-center gap-3 flex-shrink-0">
          <BrandLogo
            size="lg"
            className={
              heroScrolled
                ? '[&_.brand-wordmark]:text-[#1B2D5E]'
                : '[&_.brand-wordmark]:text-white [&_.brand-wordmark]:drop-shadow-sm'
            }
          />
        </div>

        <nav className="hidden lg:flex items-center gap-2 lg:gap-3">
          {NAV.map((item) => (
            <Link
              key={item.label}
              to={item.hash || item.to}
              onClick={(e) => handleClick(item, e)}
              className={linkClass(item)}
              style={linkStyle(item)}
            >
              <span className="flex flex-col items-center">
                {item.label}
                {isActive(item) && (
                  <span className="block h-0.5 w-full bg-[#E8651A] rounded-full mt-1" aria-hidden />
                )}
              </span>
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 flex-shrink-0">
          <a
            href={`tel:${BRAND.contact.phone}`}
            className="hidden md:inline-flex items-center gap-2.5 bg-[#E8651A] hover:bg-[#C05A18] text-white font-bold text-base lg:text-[17px] px-7 lg:px-8 py-3.5 lg:py-4 rounded-full transition-all duration-200 hover:-translate-y-0.5 shadow-lg shadow-orange-900/30 whitespace-nowrap"
          >
            <Phone size={18} />
            Call Now
          </a>

          <button
            type="button"
            className={`lg:hidden p-2 ${heroScrolled ? 'text-[#1B2D5E]' : 'text-white'}`}
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="lg:hidden bg-white border-t border-gray-100 px-4 py-3 flex flex-col gap-1 shadow-md">
          {NAV.map((item) => (
            <Link
              key={item.label}
              to={item.hash || item.to}
              onClick={(e) => handleClick(item, e)}
              className={`px-3 py-2.5 text-[14px] font-medium font-body transition-colors duration-150 hover:text-[#E8651A] ${isActive(item) ? 'text-[#E8651A] font-bold' : ''}`}
              style={{ color: isActive(item) ? '#E8651A' : '#1B2D5E' }}
            >
              {item.label}
            </Link>
          ))}
          <a
            href={`tel:${BRAND.contact.phone}`}
            className="mt-2 px-3 py-2.5 text-white rounded-lg font-medium text-center font-body"
            style={{ background: '#E8651A' }}
          >
            Call {BRAND.contact.phoneDisplay}
          </a>
        </nav>
      )}
    </header>
  );
};

export default AtithiNavbar;
